import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  FaBuilding,
  FaCompressAlt,
  FaCrosshairs,
  FaEdit,
  FaExpandAlt,
  FaMinus,
  FaPlus,
  FaSitemap,
  FaTrash,
  FaUserTie,
} from "react-icons/fa";
import { useConfirm, useNotify } from "../../components/ConfirmModal";
import { Avatar, Empty, Modal, PageHead } from "./WorkUi";
import { errorMessage } from "./workApi";
import "../dm/dm.css";
import "./work.css";
import "./orgChart.css";

/** Lookups over the org tree members: by id, direct reports, manager chain and team size. */
function buildOrg(members) {
  const byId = new Map(members.map((m) => [m.User_Id, m]));
  const children = new Map();
  members.forEach((m) => {
    const boss = m.Reports_To_User_Id;
    if (boss && byId.has(boss) && boss !== m.User_Id) {
      if (!children.has(boss)) children.set(boss, []);
      children.get(boss).push(m);
    }
  });
  const reportsOf = (id) => children.get(id) || [];

  const chainOf = (id) => {
    const chain = [];
    const seen = new Set([id]);
    let current = byId.get(id);
    while (current?.Reports_To_User_Id && byId.has(current.Reports_To_User_Id) && !seen.has(current.Reports_To_User_Id)) {
      seen.add(current.Reports_To_User_Id);
      current = byId.get(current.Reports_To_User_Id);
      chain.unshift(current);
    }
    return chain;
  };

  const teamSizeCache = new Map();
  const teamSize = (id) => {
    if (teamSizeCache.has(id)) return teamSizeCache.get(id);
    let count = 0;
    const seen = new Set([id]);
    const stack = [...reportsOf(id)];
    while (stack.length) {
      const m = stack.pop();
      if (seen.has(m.User_Id)) continue;
      seen.add(m.User_Id);
      count += 1;
      stack.push(...reportsOf(m.User_Id));
    }
    teamSizeCache.set(id, count);
    return count;
  };

  return { byId, reportsOf, chainOf, teamSize };
}

function PersonLink({ person, profileLink, className, backLabel = "Back to org chart" }) {
  const location = useLocation();
  if (!profileLink) return <span className={className}>{person.Name}</span>;
  return (
    <Link
      className={className}
      to={profileLink(person.User_Id)}
      state={{ backTo: `${location.pathname}${location.search}`, backLabel }}
    >
      {person.Name}
    </Link>
  );
}

function PersonNode({ person, reports, open, onToggle, onFocus, profileLink, current, match, note }) {
  return (
    <div className={`oc-node${current ? " is-current" : ""}${match ? " is-match" : ""}`}>
      <div className="oc-node__main">
        <Avatar name={person.Name} size={34} />
        <div className="oc-node__body">
          <PersonLink person={person} profileLink={profileLink} className="oc-node__name" />
          <div className="oc-node__role">
            {person.Is_Owner ? <span className="oc-owner-tag">Owner</span> : null}
            {person.Designation || (person.Is_Owner ? "" : "—")}
          </div>
          {person.Department ? <div className="oc-node__dept">{person.Department}</div> : null}
          {note ? <div className="oc-node__note">{note}</div> : null}
        </div>
        {onFocus ? (
          <button
            type="button"
            className="oc-node__focus"
            onClick={() => onFocus(person.User_Id)}
            title={`Show ${person.Name}'s reporting line`}
            aria-label={`Show ${person.Name}'s reporting line`}
          >
            <FaCrosshairs aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {reports > 0 && onToggle ? (
        <button
          type="button"
          className="oc-node__toggle"
          aria-expanded={open}
          onClick={() => onToggle(person.User_Id)}
          title={open ? "Hide team" : "Show team"}
          aria-label={`${open ? "Hide" : "Show"} ${person.Name}'s team (${reports} direct report${reports === 1 ? "" : "s"})`}
        >
          {open ? <FaMinus aria-hidden="true" /> : <FaPlus aria-hidden="true" />}
          {reports}
        </button>
      ) : null}
    </div>
  );
}

function Branch({ person, org, isOpen, onToggle, onFocus, profileLink, matches, currentId, note, depth = 0, seen }) {
  const reports = org.reportsOf(person.User_Id).filter((m) => !seen.has(m.User_Id));
  const open = reports.length > 0 && isOpen(person.User_Id) && depth < 40;
  const nextSeen = open ? new Set([...seen, ...reports.map((m) => m.User_Id)]) : seen;
  return (
    <li>
      <PersonNode
        person={person}
        reports={reports.length}
        open={open}
        onToggle={onToggle}
        onFocus={onFocus}
        profileLink={profileLink}
        current={person.User_Id === currentId}
        match={matches?.has(person.User_Id)}
        note={note}
      />
      {open ? (
        <ul>
          {reports.map((m) => (
            <Branch
              key={m.User_Id}
              person={m}
              org={org}
              isOpen={isOpen}
              onToggle={onToggle}
              onFocus={onFocus}
              profileLink={profileLink}
              matches={matches}
              currentId={currentId}
              depth={depth + 1}
              seen={nextSeen}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function sortByName(list) {
  return [...list].sort((a, b) => (a.Name || "").localeCompare(b.Name || ""));
}

/** Departments master for the company: add, rename and remove (admins and owners). */
function DepartmentsModal({ api, departments, members, onClose, onChanged }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const counts = useMemo(() => {
    const out = new Map();
    members.forEach((m) => m.Department_Id && out.set(Number(m.Department_Id), (out.get(Number(m.Department_Id)) || 0) + 1));
    return out;
  }, [members]);

  const run = async (fn, success) => {
    setBusy(true);
    try {
      await fn();
      await onChanged();
      notify.success(success);
      return true;
    } catch (err) {
      notify.error(errorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const value = name.trim();
    if (!value) return;
    if (await run(() => api.createDepartment({ Department_Name: value }), "Department added")) setName("");
  };

  const rename = async (e) => {
    e.preventDefault();
    const value = editing?.name.trim();
    const dept = departments.find((d) => d.Department_Id === editing?.id);
    if (!dept || !value || value === dept.Department_Name) {
      setEditing(null);
      return;
    }
    if (await run(() => api.updateDepartment(dept.Department_Id, { Department_Name: value }), "Department renamed")) {
      setEditing(null);
    }
  };

  const remove = async (d) => {
    const people = counts.get(Number(d.Department_Id)) || 0;
    const ok = await confirm({
      title: "Remove department",
      message: `Remove "${d.Department_Name}"?${people ? ` ${people} ${people === 1 ? "person" : "people"} will have no department.` : ""}`,
    });
    if (ok) await run(() => api.deleteDepartment(d.Department_Id), "Department removed");
  };

  return (
    <Modal title="Departments" onClose={onClose}>
      <p className="wk-muted" style={{ marginTop: 0 }}>
        These appear in the Department dropdown on the employee form and in the org chart filter.
      </p>
      <ul className="oc-dept-list">
        {departments.map((d) =>
          editing?.id === d.Department_Id ? (
            <li key={d.Department_Id}>
              <form className="oc-dept-add" style={{ flex: 1 }} onSubmit={rename}>
                <input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  aria-label={`New name for ${d.Department_Name}`}
                  maxLength={100}
                  autoFocus
                />
                <button type="submit" className="oc-dept-add__save" disabled={busy || !editing.name.trim()}>
                  Save
                </button>
                <button type="button" className="oc-dept-add__cancel" onClick={() => setEditing(null)} disabled={busy}>
                  Cancel
                </button>
              </form>
            </li>
          ) : (
            <li key={d.Department_Id}>
              <span className="oc-dept-list__name">{d.Department_Name}</span>
              <span className="wk-muted">{counts.get(Number(d.Department_Id)) || 0} people</span>
              <button
                type="button"
                className="dmp-link"
                onClick={() => setEditing({ id: d.Department_Id, name: d.Department_Name })}
                disabled={busy}
                aria-label={`Rename ${d.Department_Name}`}
              >
                <FaEdit aria-hidden="true" />
              </button>
              <button type="button" className="dmp-link" onClick={() => remove(d)} disabled={busy} aria-label={`Remove ${d.Department_Name}`}>
                <FaTrash aria-hidden="true" />
              </button>
            </li>
          )
        )}
        {!departments.length ? <li className="wk-muted">No departments yet.</li> : null}
      </ul>
      <form className="oc-dept-add" onSubmit={add}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New department"
          aria-label="New department"
          maxLength={100}
        />
        <button type="submit" className="oc-dept-add__save" disabled={busy || !name.trim()}>
          <FaPlus aria-hidden="true" /> Add
        </button>
      </form>
    </Modal>
  );
}

/**
 * Org chart for HRMS: the whole organization (optionally one department) or one person's reporting
 * line (managers above, peers, team below). ``profileLink(userId)`` returns the employee profile path.
 */
function OrgChart({ api, profileLink, editLink }) {
  const notify = useNotify();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [tree, setTree] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [savingManager, setSavingManager] = useState(false);
  const [showDepartments, setShowDepartments] = useState(false);

  const personId = Number(params.get("person")) || null;
  const view = personId ? "person" : "org";
  const deptId = Number(params.get("dept")) || null;

  const setParam = useCallback(
    (changes) => {
      const next = new URLSearchParams(params);
      Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, String(v)) : next.delete(k)));
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const load = useCallback(async () => {
    try {
      const [t, d] = await Promise.all([api.orgTree(), api.departments().catch(() => [])]);
      setTree(t);
      setDepartments(d || []);
      setError("");
      return t;
    } catch (err) {
      setError(errorMessage(err, "Could not load the org chart"));
      return null;
    }
  }, [api]);

  const members = useMemo(() => tree?.members || [], [tree]);
  const org = useMemo(() => buildOrg(members), [members]);

  const resetExpanded = useCallback((list, levels, alsoOpen) => {
    const o = buildOrg(list);
    const open = new Set(alsoOpen ? [alsoOpen] : []);
    const walk = (people, depth) => {
      if (depth >= levels) return;
      people.forEach((m) => {
        const reports = o.reportsOf(m.User_Id);
        if (reports.length && !open.has(m.User_Id)) {
          open.add(m.User_Id);
          walk(reports, depth + 1);
        }
      });
    };
    walk(list.filter((m) => !m.Reports_To_User_Id || !o.byId.has(m.Reports_To_User_Id)), 0);
    setExpanded(open);
  }, []);

  const initialPerson = useRef(personId);
  useEffect(() => {
    load().then((t) => t && resetExpanded(t.members || [], 2, initialPerson.current));
  }, [load, resetExpanded]);

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const focus = useCallback(
    (id) => {
      setExpanded((prev) => new Set([...prev, id]));
      setParam({ person: id });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [setParam]
  );

  // Organization view: people in the chosen department; anyone whose manager is outside it starts a branch.
  const scoped = useMemo(
    () => (deptId ? members.filter((m) => Number(m.Department_Id) === deptId) : members),
    [members, deptId]
  );
  const scopedOrg = useMemo(() => (deptId ? buildOrg(scoped) : org), [deptId, scoped, org]);
  const roots = useMemo(
    () => sortByName(scoped.filter((m) => !m.Reports_To_User_Id || !scopedOrg.byId.has(m.Reports_To_User_Id))),
    [scoped, scopedOrg]
  );
  const leaders = roots.filter((m) => scopedOrg.reportsOf(m.User_Id).length > 0);
  const unplaced = roots.filter((m) => scopedOrg.reportsOf(m.User_Id).length === 0);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return new Set(
      scoped
        .filter((m) => [m.Name, m.Designation, m.Department, m.Email].some((v) => (v || "").toLowerCase().includes(q)))
        .map((m) => m.User_Id)
    );
  }, [query, scoped]);

  // Search opens the branches leading to every match.
  const openForSearch = useMemo(() => {
    if (!matches) return null;
    const open = new Set();
    matches.forEach((id) => scopedOrg.chainOf(id).forEach((m) => open.add(m.User_Id)));
    return open;
  }, [matches, scopedOrg]);

  const isOpen = useCallback(
    (id) => expanded.has(id) || Boolean(openForSearch?.has(id)),
    [expanded, openForSearch]
  );

  const expandAll = () => setExpanded(new Set(members.filter((m) => org.reportsOf(m.User_Id).length).map((m) => m.User_Id)));
  const collapseAll = () => setExpanded(new Set());

  const person = personId ? org.byId.get(personId) : null;
  const chain = person ? org.chainOf(person.User_Id) : [];
  const manager = chain.length ? chain[chain.length - 1] : null;
  const peers = manager ? sortByName(org.reportsOf(manager.User_Id).filter((m) => m.User_Id !== person.User_Id)) : [];
  const canEditPerson = Boolean(
    person && tree && !person.Is_Owner && (tree.can_edit || (person.In_My_Team && person.User_Id !== tree.me))
  );
  const managerOptions = useMemo(() => {
    if (!person) return [];
    const blocked = new Set([person.User_Id]);
    const stack = [...org.reportsOf(person.User_Id)];
    while (stack.length) {
      const m = stack.pop();
      if (blocked.has(m.User_Id)) continue;
      blocked.add(m.User_Id);
      stack.push(...org.reportsOf(m.User_Id));
    }
    return sortByName(members.filter((m) => !blocked.has(m.User_Id)));
  }, [person, org, members]);

  const changeManager = async (value) => {
    if (!person) return;
    setSavingManager(true);
    try {
      await api.updateOrgMember(person.User_Id, { Reports_To_User_Id: value ? Number(value) : null });
      await load();
      notify.success("Manager updated");
    } catch (err) {
      notify.error(errorMessage(err, "Could not change the manager"));
    } finally {
      setSavingManager(false);
    }
  };

  const withManagers = members.filter((m) => m.Reports_To_User_Id && org.byId.has(m.Reports_To_User_Id)).length;

  return (
    <div className="dmp wk oc">
      <PageHead
        title="Org Chart"
        info="Who reports to whom. Set a manager with 'Reports to' on the employee form, or open a person here and change it."
      >
        <div className="wk-seg" role="group" aria-label="Chart view">
          <button type="button" aria-pressed={view === "org"} onClick={() => setParam({ person: null })}>
            Organization
          </button>
          <button
            type="button"
            aria-pressed={view === "person"}
            onClick={() => {
              const first = personId || tree?.me || members[0]?.User_Id;
              if (first) setParam({ person: first });
            }}
            disabled={!members.length}
          >
            Individual
          </button>
        </div>
        {tree?.can_edit ? (
          <button type="button" className="dmp-btn dmp-btn--ghost" onClick={() => setShowDepartments(true)}>
            <FaBuilding aria-hidden="true" /> Departments
          </button>
        ) : null}
      </PageHead>
      {showDepartments ? (
        <DepartmentsModal
          api={api}
          departments={departments}
          members={members}
          onClose={() => setShowDepartments(false)}
          onChanged={load}
        />
      ) : null}

      {error ? <div className="dmp-error">{error}</div> : null}

      {!tree ? (
        error ? null : <div className="dmp-card wk-muted">Loading…</div>
      ) : !members.length ? (
        <div className="dmp-card">
          <Empty icon={FaSitemap} title="No active employees yet">
            Add employees first; the chart shows everyone who is active.
          </Empty>
        </div>
      ) : view === "org" ? (
        <>
          <div className="oc-stats" aria-label="Summary">
            <span>
              <strong>{members.length}</strong> people
            </span>
            <span>
              <strong>{withManagers}</strong> with a manager
            </span>
            <span>
              <strong>{members.length - withManagers}</strong> at the top level
            </span>
            {departments.length ? (
              <span>
                <strong>{departments.length}</strong> departments
              </span>
            ) : null}
          </div>

          <div className="oc-toolbar">
            <label className="wk-sr-only" htmlFor="oc-search">
              Find a person
            </label>
            <input
              id="oc-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find by name, designation or department"
            />
            {departments.length ? (
              <>
                <label className="wk-sr-only" htmlFor="oc-dept">
                  Department
                </label>
                <select id="oc-dept" value={deptId || ""} onChange={(e) => setParam({ dept: e.target.value })}>
                  <option value="">All departments</option>
                  {departments.map((d) => (
                    <option key={d.Department_Id} value={d.Department_Id}>
                      {d.Department_Name}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
            <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={expandAll}>
              <FaExpandAlt aria-hidden="true" /> Expand all
            </button>
            <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" onClick={collapseAll}>
              <FaCompressAlt aria-hidden="true" /> Collapse all
            </button>
            {matches ? (
              <span className="wk-muted" role="status">
                {matches.size} match{matches.size === 1 ? "" : "es"}
              </span>
            ) : null}
          </div>

          {!scoped.length ? (
            <div className="dmp-card">
              <Empty icon={FaSitemap} title="Nobody in this department yet" />
            </div>
          ) : (
            <>
              {leaders.length ? (
                <div className="dmp-card oc-canvas">
                  <ul className="oc-tree">
                    {leaders.map((m) => {
                      const outsideBoss = deptId && m.Reports_To_User_Id ? org.byId.get(m.Reports_To_User_Id) : null;
                      return (
                        <Branch
                          key={m.User_Id}
                          person={m}
                          org={scopedOrg}
                          isOpen={isOpen}
                          onToggle={toggle}
                          onFocus={focus}
                          profileLink={profileLink}
                          matches={matches}
                          note={outsideBoss ? `Reports to ${outsideBoss.Name}` : null}
                          seen={new Set([m.User_Id])}
                        />
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <div className="wk-banner">
                  No reporting lines yet. Open a person below (or edit an employee) and set who they report to.
                </div>
              )}

              {unplaced.length ? (
                <div className="dmp-card oc-unplaced">
                  <h3 className="dmp-card__title">
                    {leaders.length ? "No manager and no team" : "People"} ({unplaced.length})
                  </h3>
                  <div className="oc-grid">
                    {unplaced.map((m) => {
                      const outsideBoss = deptId && m.Reports_To_User_Id ? org.byId.get(m.Reports_To_User_Id) : null;
                      return (
                        <PersonNode
                          key={m.User_Id}
                          person={m}
                          reports={0}
                          onFocus={focus}
                          profileLink={profileLink}
                          match={matches?.has(m.User_Id)}
                          note={outsideBoss ? `Reports to ${outsideBoss.Name}` : null}
                        />
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </>
      ) : !person ? (
        <div className="dmp-card">
          <Empty icon={FaUserTie} title="This person is not in the org chart">
            Only active employees appear here.{" "}
            <button type="button" className="dmp-link" onClick={() => setParam({ person: null })}>
              Show the whole organization
            </button>
          </Empty>
        </div>
      ) : (
        <>
          <div className="oc-toolbar">
            <label htmlFor="oc-person">Person</label>
            <select id="oc-person" value={person.User_Id} onChange={(e) => focus(Number(e.target.value))}>
              {sortByName(members).map((m) => (
                <option key={m.User_Id} value={m.User_Id}>
                  {m.Name}
                  {m.Designation ? ` · ${m.Designation}` : ""}
                </option>
              ))}
            </select>
            {canEditPerson ? (
              <>
                <label htmlFor="oc-manager">Reports to</label>
                <select
                  id="oc-manager"
                  value={person.Reports_To_User_Id || ""}
                  onChange={(e) => changeManager(e.target.value)}
                  disabled={savingManager}
                >
                  <option value="">No manager (top level)</option>
                  {managerOptions.map((m) => (
                    <option key={m.User_Id} value={m.User_Id}>
                      {m.Name}
                      {m.Designation ? ` · ${m.Designation}` : ""}
                    </option>
                  ))}
                </select>
              </>
            ) : person.Is_Owner ? (
              <span className="wk-muted">Company owner: always at the top of the chart.</span>
            ) : null}
          </div>

          <div className="wk-grid-2">
            <div className="dmp-card oc-canvas">
              {chain.length ? (
                <ol className="oc-chain" aria-label="Managers">
                  {chain.map((m) => (
                    <li key={m.User_Id}>
                      <PersonNode person={m} reports={0} onFocus={focus} profileLink={profileLink} />
                    </li>
                  ))}
                </ol>
              ) : null}
              <ul className="oc-tree">
                <Branch
                  person={person}
                  org={org}
                  isOpen={isOpen}
                  onToggle={toggle}
                  onFocus={focus}
                  profileLink={profileLink}
                  currentId={person.User_Id}
                  seen={new Set([person.User_Id])}
                />
              </ul>
            </div>

            <div className="dmp-card">
              <h3 className="dmp-card__title">{person.Name}</h3>
              <dl className="oc-facts">
                <dt>Designation</dt>
                <dd>{person.Designation || "—"}</dd>
                <dt>Department</dt>
                <dd>{person.Department || "—"}</dd>
                {person.Primary_Area ? (
                  <>
                    <dt>Main work area</dt>
                    <dd>{person.Primary_Area}</dd>
                  </>
                ) : null}
                <dt>Reports to</dt>
                <dd>
                  {person.Is_Owner ? (
                    "Company owner (top level)"
                  ) : manager ? (
                    <button type="button" className="dmp-link" onClick={() => focus(manager.User_Id)}>
                      {manager.Name}
                    </button>
                  ) : (
                    "No manager (top level)"
                  )}
                </dd>
                <dt>Direct reports</dt>
                <dd>{org.reportsOf(person.User_Id).length}</dd>
                <dt>Whole team</dt>
                <dd>{org.teamSize(person.User_Id)}</dd>
              </dl>
              <div className="oc-actions">
                {editLink && (tree.can_edit || canEditPerson) ? (
                  <Link
                    to={editLink(person.User_Id)}
                    state={{ edit: true, backTo: `${location.pathname}${location.search}`, backLabel: "Back to org chart" }}
                    className="dmp-btn dmp-btn--sm"
                  >
                    <FaEdit aria-hidden="true" /> Edit employee
                  </Link>
                ) : null}
                {profileLink ? (
                  <Link
                    to={profileLink(person.User_Id)}
                    state={{ backTo: `${location.pathname}${location.search}`, backLabel: "Back to org chart" }}
                    className="dmp-btn dmp-btn--ghost dmp-btn--sm"
                  >
                    Open employee profile
                  </Link>
                ) : null}
              </div>

              <h4 className="oc-subtitle">Peers{manager ? ` (also report to ${manager.Name})` : ""}</h4>
              {peers.length ? (
                <div className="oc-chips">
                  {peers.map((m) => (
                    <button key={m.User_Id} type="button" className="wk-chip" onClick={() => focus(m.User_Id)}>
                      {m.Name}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="wk-muted">{manager ? "Nobody else reports to this manager." : "—"}</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Reporting line card for an employee profile: manager, managers above them, direct reports and team
 * size, with a link into the org chart. ``chartLink(userId)`` returns the org chart path for that person.
 */
export function ReportingSummary({ api, userId, profileLink, chartLink }) {
  const [tree, setTree] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    api
      .orgTree()
      .then((t) => !cancelled && setTree(t))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [api, userId]);

  const org = useMemo(() => buildOrg(tree?.members || []), [tree]);

  if (failed) return <p className="oc-muted">Could not load the reporting line.</p>;
  if (!tree) return <p className="oc-muted">Loading…</p>;
  const me = org.byId.get(Number(userId));
  if (!me) return <p className="oc-muted">Not in the org chart yet (only active employees appear).</p>;

  const chain = org.chainOf(me.User_Id);
  const manager = chain[chain.length - 1];
  const reports = sortByName(org.reportsOf(me.User_Id));
  const link = (m) => (
    <PersonLink person={m} profileLink={profileLink} className="oc-person-link" backLabel={`Back to ${me.Name}`} />
  );

  return (
    <div className="oc-summary">
      <div className="oc-summary__grid">
        <div>
          <span className="oc-summary__label">Reports to</span>
          <span className="oc-summary__value">
            {manager ? (
              <>
                {link(manager)}
                {manager.Designation ? <span className="oc-muted"> · {manager.Designation}</span> : null}
              </>
            ) : (
              "No manager (top level)"
            )}
          </span>
        </div>
        <div>
          <span className="oc-summary__label">Direct reports</span>
          <span className="oc-summary__value">{reports.length}</span>
        </div>
        <div>
          <span className="oc-summary__label">Whole team</span>
          <span className="oc-summary__value">{org.teamSize(me.User_Id)}</span>
        </div>
      </div>

      {chain.length > 1 ? (
        <div className="oc-summary__row">
          <span className="oc-summary__label">Reporting line</span>
          <ol className="oc-breadcrumb">
            {chain.map((m) => (
              <li key={m.User_Id}>{link(m)}</li>
            ))}
            <li aria-current="true">{me.Name}</li>
          </ol>
        </div>
      ) : null}

      {reports.length ? (
        <div className="oc-summary__row">
          <span className="oc-summary__label">Team</span>
          <div className="oc-chips">
            {reports.map((m) => (
              <span key={m.User_Id} className="oc-chip">
                {link(m)}
                {m.Designation ? <span className="oc-muted"> · {m.Designation}</span> : null}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {chartLink ? (
        <Link to={chartLink(me.User_Id)} className="oc-chart-link">
          <FaSitemap aria-hidden="true" /> View in org chart
        </Link>
      ) : null}
    </div>
  );
}

export default OrgChart;
